import React from 'react'
import styled from 'styled-components'
import ExperienceCard from '../Cards/ExperienceCard';
import { experiences } from '../../data/constants';
import useScrollReveal from '../../hooks/useScrollReveal';

const Container = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    position: relative;
    z-index: 1;
    align-items: center;
    padding: 40px 0 80px;

    @media (max-width: 960px) {
        padding: 20px 0;
    }
`;

const Wrapper = styled.div`
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    width: 100%;
    max-width: 1100px;
    padding: 40px 16px;
    gap: 12px;
`;

const Title = styled.h2`
    font-size: 42px;
    text-align: center;
    font-weight: 700;
    margin-top: 20px;
    color: ${({ theme }) => theme.text_primary};
    letter-spacing: -0.5px;

    @media (max-width: 768px) {
        margin-top: 12px;
        font-size: 32px;
    }
`;

const Desc = styled.div`
    font-size: 17px;
    text-align: center;
    max-width: 600px;
    color: ${({ theme }) => theme.text_secondary};
    line-height: 1.6;

    @media (max-width: 768px) {
        font-size: 15px;
    }
`;

const TimelineContainer = styled.div`
    width: 100%;
    max-width: 1000px;
    margin-top: 24px;
    position: relative;
`;

const TimelineLine = styled.div`
    position: absolute;
    left: 50%;
    top: 0;
    bottom: 0;
    width: 2px;
    transform: translateX(-50%) scaleY(${({ $visible }) => ($visible ? 1 : 0)});
    transform-origin: top;
    background: linear-gradient(180deg, #7c6af7, #854CE6, #7c6af7);
    transition: transform 1.4s cubic-bezier(0.22, 1, 0.36, 1);

    @media (max-width: 768px) {
        left: 16px;
        transform: translateX(0) scaleY(${({ $visible }) => ($visible ? 1 : 0)});
    }
`;

const TimelineRow = styled.div`
    display: flex;
    align-items: flex-start;
    width: 100%;
    position: relative;
    margin-bottom: 40px;

    &:last-child {
        margin-bottom: 0;
    }

    @media (max-width: 768px) {
        padding-left: 40px;
    }
`;

const CardLeft = styled.div`
    width: calc(50% - 30px);
    opacity: ${({ $visible }) => ($visible ? 1 : 0)};
    transform: translateX(${({ $visible }) => ($visible ? '0' : '-60px')});
    transition: opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1),
                transform 0.7s cubic-bezier(0.22, 1, 0.36, 1);
    transition-delay: ${({ $delay }) => $delay || '0s'};

    @media (max-width: 768px) {
        width: 100%;
        transform: translateX(${({ $visible }) => ($visible ? '0' : '-30px')});
    }
`;

const CardRight = styled.div`
    width: calc(50% - 30px);
    margin-left: auto;
    opacity: ${({ $visible }) => ($visible ? 1 : 0)};
    transform: translateX(${({ $visible }) => ($visible ? '0' : '60px')});
    transition: opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1),
                transform 0.7s cubic-bezier(0.22, 1, 0.36, 1);
    transition-delay: ${({ $delay }) => $delay || '0s'};

    @media (max-width: 768px) {
        width: 100%;
        margin-left: 0;
        transform: translateX(${({ $visible }) => ($visible ? '0' : '-30px')});
    }
`;

const DotCenter = styled.div`
    position: absolute;
    left: 50%;
    top: 18px;
    transform: translateX(-50%);
    z-index: 2;

    @media (max-width: 768px) {
        left: 16px;
        transform: translateX(-50%);
    }
`;

const Dot = styled.div`
    width: 16px;
    height: 16px;
    border-radius: 50%;
    border: 3px solid #7c6af7;
    background: ${({ theme }) => theme.bg};
    position: relative;
    opacity: ${({ $visible }) => ($visible ? 1 : 0)};
    transform: scale(${({ $visible }) => ($visible ? 1 : 0)});
    transition: all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
    transition-delay: ${({ $delay }) => $delay || '0s'};

    &::after {
        content: '';
        position: absolute;
        inset: -6px;
        border-radius: 50%;
        background: rgba(133, 76, 230, 0.2);
        opacity: ${({ $visible }) => ($visible ? 1 : 0)};
        transition: opacity 0.5s ease 0.3s;
    }
`;

const TimelineEntry = ({ experience, index }) => {
    const [ref, isVisible] = useScrollReveal({ threshold: 0.15 });
    const delay = `${index * 0.15}s`;
    const isLeft = index % 2 === 0;

    const CardWrapper = isLeft ? CardLeft : CardRight;

    return (
        <TimelineRow ref={ref}>
            <DotCenter>
                <Dot $visible={isVisible} $delay={`${index * 0.15 + 0.1}s`} />
            </DotCenter>
            <CardWrapper $visible={isVisible} $delay={delay}>
                <ExperienceCard experience={experience} />
            </CardWrapper>
        </TimelineRow>
    );
};

const Experience = () => {
    const [lineRef, lineVisible] = useScrollReveal({ threshold: 0.05 });

    return (
        <Container id="experience">
            <Wrapper>
                <Title>Experience</Title>
                <Desc>
                    My work experience as a software engineer across different companies and projects.
                </Desc>
                <TimelineContainer ref={lineRef}>
                    <TimelineLine $visible={lineVisible} />
                    {experiences.map((experience, idx) => (
                        <TimelineEntry
                            key={experience.id}
                            experience={experience}
                            index={idx}
                        />
                    ))}
                </TimelineContainer>
            </Wrapper>
        </Container>
    )
}

export default Experience
